import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from "react";
import { useId } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * Buttons follow the app: bold, sentence case, 8pt corners, solid fill.
 *
 * `primary` is the orange of New Mission. `destructive` is a tinted red rather
 * than the app's solid red: on the web it sits in dense rows beside other
 * content, where a block of solid red would shout over everything else.
 */
export function Button({
  children,
  variant = "primary",
  busy = false,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "destructive";
  busy?: boolean;
}) {
  const base =
    "h-11 px-5 inline-flex items-center justify-center gap-2 rounded-lg font-semibold " +
    "text-[0.95rem] transition-[filter,background-color] select-none " +
    "disabled:opacity-45 disabled:cursor-not-allowed";

  const variants = {
    primary: "bg-accent text-on-accent hover:brightness-105 active:brightness-95",
    secondary: "bg-panel-raised text-ink hover:brightness-110 active:brightness-95",
    destructive: "bg-danger/15 text-danger hover:bg-danger/25",
  } as const;

  return (
    <button className={`${base} ${variants[variant]} ${className}`} disabled={busy || props.disabled} {...props}>
      {busy && (
        <span
          aria-hidden
          className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
      )}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[0.9rem] font-semibold text-ink">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p role="alert" className="text-[0.85rem] text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[0.85rem] text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}

const controlBase =
  "h-12 px-3.5 bg-panel border rounded-lg text-ink text-[1rem] placeholder:text-ink-faint " +
  "transition-colors outline-none";

const controlState = (invalid: boolean) =>
  invalid ? "border-danger" : "border-rule focus:border-accent";

export function Input({
  invalid = false,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={`${controlBase} ${controlState(invalid)} ${className}`}
      {...props}
    />
  );
}

export function Select({
  invalid = false,
  className = "",
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      aria-invalid={invalid || undefined}
      className={`${controlBase} ${controlState(invalid)} ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

export function Textarea({
  invalid = false,
  className = "",
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={
        `px-3.5 py-3 bg-panel border rounded-lg text-ink text-[1rem] placeholder:text-ink-faint ` +
        `transition-colors outline-none resize-y min-h-24 ${controlState(invalid)} ${className}`
      }
      {...props}
    />
  );
}

/** Top-level error, for failures that belong to the request and not a field. */
export function Alert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="bg-danger/12 border border-danger/40 px-4 py-3 rounded-lg text-[0.95rem] text-danger">
      {children}
    </div>
  );
}

/**
 * A grouped surface — the web version of the app's secondarySystemBackground
 * card. The border is kept deliberately: on a dim matte monitor the fill
 * difference between card and page nearly vanishes, and the edge is what
 * still separates them.
 */
export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`bg-panel border border-rule-soft rounded-xl ${className}`}>{children}</div>;
}

/** Page title, as the app sets them: large, bold, sentence case. */
export function PageTitle({ children, detail }: { children: ReactNode; detail?: ReactNode }) {
  return (
    <div>
      <h1 className="text-[2rem] leading-tight font-bold tracking-tight text-ink">{children}</h1>
      {detail && <p className="mt-1.5 text-[1rem] text-ink-soft">{detail}</p>}
    </div>
  );
}

/**
 * The app's SummaryTile: an icon in an accent-tinted rounded square, a
 * secondary label, and the value in bold monospace.
 */
export function StatTile({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center text-center gap-2 py-5 px-3">
      <span className="grid place-items-center w-14 h-14 rounded-lg bg-accent/20 text-accent-text">
        <Icon size={30} strokeWidth={2.2} aria-hidden />
      </span>
      <span className="text-[0.9rem] text-ink-soft">{label}</span>
      <span className="font-mono font-bold text-[1.25rem] text-ink tabular-nums">{value}</span>
    </div>
  );
}

/** Small state pill. The word is always there, so colour never carries it alone. */
export function StatusChip({ status }: { status: "serviceable" | "unserviceable" | "retired" }) {
  const styles = {
    serviceable: "bg-good/15 text-good",
    unserviceable: "bg-danger/15 text-danger",
    retired: "bg-muted/15 text-ink-soft",
  } as const;
  const labels = {
    serviceable: "Serviceable",
    unserviceable: "Unserviceable",
    retired: "Retired",
  } as const;

  return <Pill className={styles[status]}>{labels[status]}</Pill>;
}

export function Pill({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[0.78rem] font-semibold px-2 py-0.5 rounded-full ${className}`}>
      {children}
    </span>
  );
}

/**
 * An iOS-style switch. A real button with `role="switch"` so it is operable by
 * keyboard and read correctly by assistive technology, not a styled checkbox.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full shrink-0 transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${
        checked ? "bg-good" : "bg-muted/40"
      }`}
    >
      <span
        aria-hidden
        className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}
